/* eslint-disable import/order */
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Components
import { Box, Button } from '@mui/material';
import AuthSequencingCard from './AuthSequencingCard';
import EditPanel from 'feature/loading-management/authorization-sequencing/edit-panel/EditPanel.connector';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { AuthContext } from 'authentication/AuthContext';

// Utils
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import { useCardChangeTracking, useUserRolePermissions } from 'hooks';
import CustomizableCard, { CARD_STATUS } from 'common/template-parts/CustomizableCard';
import { createSnack } from 'snackbar/snackbarSlice';
import AuthorizationSequencingCardContent from 'feature/loading-management/authorization-sequencing/templates/AuthorizationSequencingCardContent';
import LoadableChip from 'feature/loading-management/authorization-sequencing/templates/LoadableChip';
import AuthSequencingCardFooter from '../../templates/AuthSequencingCardFooter';
import { useUpdateAuthorizationSequencingMutation } from '../../authorizationSequencingEndpoints';

import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import EditFutureSequenceDrawer from 'feature/loading-management/authorization-sequencing/edit-panel/EditFutureSequenceDrawer';

const useNavigationBlocker = (blocker, when = true) => {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return undefined;
    const suppressBrowserUnloadPrompt = event => {
      event.stopImmediatePropagation();
    };
    window.addEventListener('beforeunload', suppressBrowserUnloadPrompt);

    const unblock = navigator.block(transaction => {
      const autoUnblockingTransaction = {
        ...transaction,
        retry() {
          unblock();
          transaction.retry();
        },
      };

      blocker(autoUnblockingTransaction);
    });

    return () => {
      window.removeEventListener('beforeunload', suppressBrowserUnloadPrompt);
      unblock();
    };
  }, [blocker, navigator, when]);
};

// can be removed when the in-card editing flag is removed and
// AuthSequencingCardFooter is used directly in the
// cardFooter prop of CustomizableCard in CardsList
const CardFooter = props => {
  const { cardRef, companyCustomerIdName, dataCompanyId, handleCardChange, locationId, productId } =
    props;
  const { isProductLoadingManage } = useUserRolePermissions();
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const dispatch = useDispatch();
  const [updateAuthorizationSequencing] = useUpdateAuthorizationSequencingMutation();
  const inCardEditingFlag = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_IN_CARD_EDITING
  );

  const mapSavedAuthorization = auth => ({
    futureSequenceDate: auth.futureSequenceDate,
    inventoryOwner:
      typeof auth.inventoryOwner === 'string'
        ? auth.inventoryOwner
        : auth.inventoryOwner?.nameShort || auth.inventoryOwner?.companyCustomerIdName || '',
    isActive: auth.active,
    productAuthorizationId: auth.productAuthorizationId,
    sequenceNumber: auth.sequenceNumber,
    supplierDirectedBlend: auth.supplierDirectedBlend,
  });

  const onClick = () => {
    setDrawerComponentProps({
      Component: drawerProps => (
        <EditPanel {...drawerProps} locationId={locationId} productId={productId} />
      ),
      forceCloseSelector: () => {},
    });
  };
  const onSave = async () => {
    const currentAuthData = cardRef?.current?.getCurrentState();

    if (!currentAuthData) {
      return;
    }

    cardRef?.current?.showSavingState();

    try {
      const body = {
        authorizationSequence: currentAuthData.sequenced.map(auth => auth.productAuthorizationId),
        futureSequence: {
          futureAuthorizationSequence: [],
          futureSequenceDate: null,
        },
        locationId,
        productId,
        supplier: {
          companyCustomerIdName,
          dataCompanyId,
        },
      };

      const response = await updateAuthorizationSequencing(body).unwrap();

      cardRef?.current?.applySavedState({
        notSequenced: (response.unsequencedAuthorizations || []).map(mapSavedAuthorization),
        sequenced: (response.sequencedAuthorizations || []).map(mapSavedAuthorization),
      });

      cardRef?.current?.showSuccessState();
      handleCardChange(locationId, productId, false);
    } catch (error) {
      const message = error?.data?.detail || error?.data?.title || error?.error || 'an error';

      cardRef?.current?.showErrorState();
      handleCardChange(locationId, productId, true);
      dispatch(
        createSnack({
          message: `Due to ${message}, your sequence has not been saved.`,
          options: { persist: true, variant: 'error' },
          title: 'Action Not Completed',
        })
      );
    }
  };

  return (
    isProductLoadingManage && (
      <>
        {inCardEditingFlag ? (
          <AuthSequencingCardFooter {...props} onCardSave={onSave} />
        ) : (
          <Button color="primary" onClick={onClick} sx={{ cursor: 'pointer' }} variant="outlined">
            Edit Sequence
          </Button>
        )}
      </>
    )
  );
};

CardFooter.propTypes = {
  authSequenceData: shape({}).isRequired,
  cardRef: shape({ current: shape({}) }),
  companyCustomerIdName: string.isRequired,
  dataCompanyId: number.isRequired,
  getCardHasChanges: func,
  handleCardChange: func.isRequired,
  isProcessing: bool,
  locationId: number.isRequired,
  productId: number.isRequired,
};

CardFooter.defaultProps = {
  cardRef: null,
  getCardHasChanges: null,
  isProcessing: false,
};

const CardsList = ({ authorizationSequencingCards }) => {
  const {
    currentAffiliate: { dataCompanyId, customerIdName: companyCustomerIdName },
  } = useContext(AuthContext);
  const authSequencingFlag = EvaluateFlag(process.env.REACT_APP_LD_SEQUENCING_AUTH_LIST);
  const [cardHeight, setCardHeight] = useState(262);
  const dispatch = useDispatch();
  const { isProductLoadingManage } = useUserRolePermissions();
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const inCardEditingFlag = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_IN_CARD_EDITING
  );
  const {
    cardRefs,
    getCardRef,
    handleCardChange,
    getCardHasChanges,
    setCardStatus,
    getCardStatus,
  } = useCardChangeTracking();
  const allowNavigationRef = useRef(false);
  const [isUnsavedDialogOpen, setIsUnsavedDialogOpen] = useState(false);
  const allChangedCards = authorizationSequencingCards.filter(card =>
    getCardHasChanges(card.locationId, card.productId)
  );
  const hasUnsavedChanges = allChangedCards.length > 0;

  const handleBlockedNavigation = useCallback(
    transaction => {
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        transaction.retry();
        return;
      }

      if (isUnsavedDialogOpen) return;

      setIsUnsavedDialogOpen(true);

      dispatch(
        openConfirmationDialog({
          cancelAction: () => {
            setIsUnsavedDialogOpen(false);
          },
          confirmAction: () => {
            const navigation = transaction;
            allowNavigationRef.current = true;
            setIsUnsavedDialogOpen(false);
            navigation?.retry?.();
          },
          header: 'Unsaved Changes',
          leftButtonActionType: 'confirm',
          leftButtonText: 'Continue',
          message:
            'Are you sure you want to leave this page?  You have unsaved Authorization Sequencing changes.',
          params: {},
          rightButtonActionType: 'cancel',
          rightButtonText: 'Keep Editing',
        })
      );
    },
    [dispatch, isUnsavedDialogOpen]
  );

  useNavigationBlocker(handleBlockedNavigation, hasUnsavedChanges);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.__authSeqHasUnsavedChanges = hasUnsavedChanges;

    return () => {
      window.__authSeqHasUnsavedChanges = false;
    };
  }, [hasUnsavedChanges]);

  return authorizationSequencingCards.length ? (
    authorizationSequencingCards.map((card, index) => {
      const cardStatus = getCardStatus(card.locationId, card.productId);
      const isCardProcessing = cardStatus === CARD_STATUS.PROGRESS;

      return authSequencingFlag ? (
        <CustomizableCard
          cardContent={
            <AuthorizationSequencingCardContent
              authSequenceData={card}
              canEdit={isProductLoadingManage && inCardEditingFlag && !isCardProcessing}
              onAuthSequenceChange={newState => {
                const cardRef = getCardRef(card.locationId, card.productId);
                const hasChanges = cardRef?.current?.compareStateToOriginal?.(newState) ?? true;
                handleCardChange(card.locationId, card.productId, hasChanges);
              }}
              onFutureAuthSequenceClick={() => {
                setDrawerComponentProps({
                  Component: drawerProps => (
                    <EditFutureSequenceDrawer
                      {...drawerProps}
                      affiliate={{ customerIdName: companyCustomerIdName, dataCompanyId }}
                      isReadOnly={!isProductLoadingManage}
                      locationId={card.locationId}
                      productId={card.productId}
                    />
                  ),
                  forceCloseSelector: () => {},
                });
              }}
              onStatusChange={status => setCardStatus(card.locationId, card.productId, status)}
              ref={getCardRef(card.locationId, card.productId)}
            />
          }
          cardFooter={
            <CardFooter
              authSequenceData={card}
              cardRef={getCardRef(card.locationId, card.productId)}
              cardRefs={cardRefs}
              companyCustomerIdName={companyCustomerIdName}
              dataCompanyId={dataCompanyId}
              getCardHasChanges={getCardHasChanges}
              handleCardChange={handleCardChange}
              isProcessing={isCardProcessing}
              locationId={card.locationId}
              productId={card.productId}
            />
          }
          cardHeader={card.nameLong}
          cardHeaderRightSideContent={<LoadableChip loadingAllowed={card.loadingAllowed} />}
          key={card.nameLong}
          status={cardStatus}
        />
      ) : (
        <AuthSequencingCard
          card={card}
          cardHeight={cardHeight}
          index={index}
          key={card.nameShort}
          setCardHeight={setCardHeight}
        />
      );
    })
  ) : (
    <Box
      alignItems="center"
      backgroundColor="graphic.lighter"
      display="flex"
      justifyContent="center"
      mt={2}
      sx={{
        '& > div': {
          ml: 0,
          mt: 0,
          px: 4,
          width: 'fit-content',
        },
        '& > div p': {
          whiteSpace: 'nowrap',
        },
      }}
      width="100%"
    >
      <NoAuthorizationsAvailable />
    </Box>
  );
};

CardsList.propTypes = {
  authorizationSequencingCards: arrayOf(shape()).isRequired,
};

export default CardsList;
